import type { MeetingListItem, MeetingParticipant } from '@fos/shared/contracts/meetings';
import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { firstName, formatDayShort, formatDuration, plural } from '../../lib/format';

/** „16:30” → „16:30”, „09:00” → „9:00” (jak w Figmie i w Dziś). */
export function clockText(time: string | null): string {
  if (!time) return '';
  const [h = '0', m = '00'] = time.split(':');
  return `${Number(h)}:${m}`;
}

/** „16:30–18:00” albo „16:30”. */
export function timeRangeText(m: Pick<MeetingListItem, 'start' | 'end'>): string {
  return m.end ? `${clockText(m.start)}–${clockText(m.end)}` : clockText(m.start);
}

/** Dzień spotkania: „Dziś”, „Jutro”, „Wczoraj” albo „pt 2.10”. */
export function dayText(date: string, now: Date = new Date()): string {
  const today = todayLocal(now);
  if (date === today) return 'Dziś';
  if (date === addDaysLocal(today, 1)) return 'Jutro';
  if (date === addDaysLocal(today, -1)) return 'Wczoraj';
  return formatDayShort(date);
}

/** Linia meta: „pt 2.10, 16:30–18:00”. */
export function meetingWhenText(m: Pick<MeetingListItem, 'date' | 'start' | 'end'>, now: Date = new Date()): string {
  return `${dayText(m.date, now)}, ${timeRangeText(m)}`;
}

/** Czas trwania spotkania („1 h 30 min”) albo null bez końca. */
export function meetingDurationText(m: Pick<MeetingListItem, 'startsAt' | 'endsAt'>): string | null {
  if (!m.endsAt) return null;
  const seconds = Math.round((new Date(m.endsAt).getTime() - new Date(m.startsAt).getTime()) / 1000);
  return seconds > 0 ? formatDuration(seconds) : null;
}

/** Uczestnicy w linii meta (Figma: „Paweł, Seban”): imiona, najwyżej 3, reszta liczbą. */
export function participantsText(list: readonly MeetingParticipant[], max = 3): string {
  if (list.length === 0) return '';
  const names = list.map((p) => firstName(p.name));
  if (names.length <= max) return names.join(', ');
  const rest = names.length - max;
  return `${names.slice(0, max).join(', ')} i ${rest} ${plural(rest, ['inna osoba', 'inne osoby', 'innych osób'])}`;
}

/** Klient · projekt (bez powtarzania klienta w jego profilu). */
export function contextText(
  m: Pick<MeetingListItem, 'client' | 'project'>,
  opts: { hideClient?: boolean } = {},
): string {
  return [opts.hideClient ? null : m.client?.name, m.project?.name].filter(Boolean).join(' · ');
}

export function meetingHref(id: string): string {
  return `/spotkania/${id}`;
}
